import {
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { Router } from '@angular/router';
import { Order, Shops, Subscription } from '@infrab4a/connect';
import { lastValueFrom, map } from 'rxjs';
import {
  KpiControllerService,
  ShopContributionResponse,
  ShopIncomeResponse,
  SubscriptionsTotalResponse
} from 'src/app/admin-api';
import { ShopOrderService } from 'src/app/connect-api/api/shop/shop-order.service';
import { ShopSubscriptionService } from 'src/app/connect-api/api/shop/shop-subscription.service';
import { Message, SubscriberUpdate, Topic } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { WebsocketService } from 'src/app/services/websocket.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-consolidated-dashboard',
  templateUrl: './consolidated-dashboard.component.html',
  styleUrl: './consolidated-dashboard.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ConsolidatedDashboardComponent implements OnInit, OnDestroy {
  @Input()
  glamSubscriptions: WritableSignal<SubscriptionsTotalResponse> =
    signal(undefined);

  @Input()
  show: WritableSignal<boolean>;

  private kpiService = inject(KpiControllerService);
  private shopOrderService = inject(ShopOrderService);
  private subscriptionService = inject(ShopSubscriptionService);
  private websocketService = inject(WebsocketService);

  private static readonly CACHE_KEY = 'b4a_glam_subs';
  private static readonly CACHE_TTL = 3 * 60 * 1000;
  private static readonly SYNC_INTERVAL = 5 * 60 * 1000;
  private syncInterval: NodeJS.Timeout;

  private readonly COUPON_CATEGORIES = ['Glampartner', 'BFlu'];

  private readonly OWN_BRANDS = [
    'glam beauty',
    'glambeauty',
    "men's",
    'malmo',
    'ella'
  ];

  shopIncome: WritableSignal<ShopIncomeResponse> = signal(undefined);
  ownBrandsIncome: WritableSignal<ShopIncomeResponse> = signal(undefined);
  couponIncome: WritableSignal<ShopIncomeResponse> = signal(undefined);
  mensSubscriptions: WritableSignal<number> = signal(undefined);
  subscriptions: WritableSignal<Array<Subscription>> = signal(undefined);
  subscriptionsTotal = computed(() => {
    if (
      this.mensSubscriptions() === undefined ||
      this.glamSubscriptions() === undefined
    )
      return undefined;
    return {
      total:
        (this.glamSubscriptions()?.total || 0) +
        (this.mensSubscriptions() || 0),
      createdAt: this.glamSubscriptions()?.createdAt || null
    } as SubscriptionsTotalResponse;
  });
  contributionResponse: WritableSignal<ShopContributionResponse> =
    signal(undefined);
  panel = signal(!inject(Router).url.includes('/dashboard'));
  interval: NodeJS.Timeout;
  ordersInterval: NodeJS.Timeout;
  orders: WritableSignal<Array<Order>> = signal(undefined);
  lastSubscriptionGlam = computed(() => {
    return this.glamSubscriptions()?.createdAt || null;
  });

  lastSubscriptionMens = computed(() =>
    this.subscriptions()
      ?.filter((s) => (s as any).subscriptionId === 3)
      .reduce((last: Date, s) => {
        if (!last || s.createdAt > last) {
          last = s.createdAt;
        }
        return last;
      }, null)
  );

  lastIncomeOrderGlam = computed(() =>
    this.orders()
      ?.filter((o) => o.shop === Shops.GLAMSHOP && (o.totalPrice || 0) > 0)
      .reduce((last: Date, s) => {
        if (!last || s.createdAt > last) {
          last = s.createdAt;
        }
        return last;
      }, null)
  );

  lastIncomeOrderMens = computed(() =>
    this.orders()
      ?.filter((o) => o.shop === Shops.MENSMARKET && (o.totalPrice || 0) > 0)
      .reduce((last: Date, s) => {
        if (!last || s.createdAt > last) {
          last = s.createdAt;
        }
        return last;
      }, null)
  );

  ownBrandsPercentage = computed(() => {
    const own = this.ownBrandsIncome()?.total || 0;
    const total = this.shopIncome()?.total || 0;
    return total > 0 ? (own / total) * 100 : 0;
  });

  couponPercentage = computed(() => {
    const coupon = this.couponIncome()?.total || 0;
    const total = this.shopIncome()?.total || 0;
    return total > 0 ? (coupon / total) * 100 : 0;
  });

  async ngOnInit() {
    if (!this.glamSubscriptions) this.glamSubscriptions = signal(undefined);
    this.updateFullscreenClasses();
    window.addEventListener('resize', this.updateFullscreenClasses);
    if (!this.panel()) {
      this.loadGlamSubscriptionsFromCache();
      this.connect();
    }
    await this.findPage(!this.panel(), true);
    this.startIntervals();
    this.startOrdersInterval();
    this.startSyncInterval();
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.updateFullscreenClasses);
    if (this.interval) {
      clearInterval(this.interval);
      delete this.interval;
    }
    if (this.ordersInterval) {
      clearInterval(this.ordersInterval);
      delete this.ordersInterval;
    }
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      delete this.syncInterval;
    }
    this.websocketService.disconnect();
  }

  updateFullscreenClasses = (): void => {
    const headerElements = Array.from(
      document.querySelectorAll('.b4a-tv-header')
    );
    const footerElements = Array.from(
      document.querySelectorAll('.b4a-tv-footer')
    );
    const fullscreenThreshold = 40;
    const isFullscreen =
      Math.abs(window.innerHeight - window.screen.height) <
        fullscreenThreshold &&
      Math.abs(window.innerWidth - window.screen.width) < fullscreenThreshold;

    headerElements.forEach((el) =>
      el.classList.toggle('b4a-tv-header--fullscreen', isFullscreen)
    );
    footerElements.forEach((el) =>
      el.classList.toggle('b4a-tv-footer--fullscreen', isFullscreen)
    );
  };

  private startIntervals() {
    if (!this.interval) {
      this.interval = setInterval(async () => {
        await this.findPage(false);
      }, 60000);
    }
  }

  private startSyncInterval() {
    if (!this.syncInterval) {
      this.syncInterval = setInterval(async () => {
        await this.findSubscriptionsRealtimeCount();
      }, ConsolidatedDashboardComponent.SYNC_INTERVAL);
    }
  }

  private loadGlamSubscriptionsFromCache() {
    try {
      const raw = localStorage.getItem(
        ConsolidatedDashboardComponent.CACHE_KEY
      );
      if (!raw) return;
      const cached = JSON.parse(raw);
      const age = Date.now() - (cached._ts || 0);
      if (age > ConsolidatedDashboardComponent.CACHE_TTL) return;
      const today = new Date().toDateString();
      const cacheDay = new Date(cached._ts).toDateString();
      if (today !== cacheDay) return;
      const data = {
        total: cached.total,
        createdAt: cached.createdAt ? new Date(cached.createdAt) : null
      } as SubscriptionsTotalResponse;
      if (!this.glamSubscriptions) this.glamSubscriptions = signal(undefined);
      this.glamSubscriptions.set(data);
    } catch (error) {
      console.error('Failed to load glam subscriptions from cache', error);
    }
  }

  private saveGlamSubscriptionsToCache(data: SubscriptionsTotalResponse) {
    try {
      localStorage.setItem(
        ConsolidatedDashboardComponent.CACHE_KEY,
        JSON.stringify({
          total: data.total,
          createdAt: data.createdAt,
          _ts: Date.now()
        })
      );
    } catch (error) {
      console.error('Failed to save glam subscriptions to cache', error);
    }
  }

  private startOrdersInterval() {
    if (!this.ordersInterval) {
      this.ordersInterval = setInterval(async () => {
        await this.findOrders();
        await Promise.all([
          this.findShopIncome(),
          this.findOwnBrandsIncome(),
          this.findCouponIncome()
        ]);
      }, 10000);
    }
  }

  async findPage(findCount = true, firstLoad = false) {
    await this.findOrders();
    const promises = [
      this.findShopIncome(),
      this.findOwnBrandsIncome(),
      this.findCouponIncome(),
      this.findMensSubscriptions()
    ];
    if (firstLoad || new Date().getMinutes() === 0)
      promises.push(this.findContributionMargin());
    if (findCount) {
      if (!this.glamSubscriptions) this.glamSubscriptions = signal(undefined);
      promises.push(this.findSubscriptionsRealtimeCount());
    }
    await Promise.all(promises);
  }

  async findOrders() {
    try {
      this.orders.set(await this.shopOrderService.paidToday());
    } catch (error) {
      console.error(error);
      if (!this.orders()) this.orders.set([]);
    }
  }

  async findShopIncome() {
    try {
      const total = this.orders().reduce(
        (sum, o) => (sum += o.totalPrice || 0),
        0
      );
      const createdAt = this.orders().at(0)?.createdAt;
      if (
        !this.shopIncome()?.total ||
        this.shopIncome().total < total ||
        !this.shopIncome()?.createdAt ||
        (createdAt &&
          this.shopIncome().createdAt.getTime() < createdAt.getTime())
      )
        this.shopIncome.set({ total, createdAt });
    } catch (error) {
      if (!this.shopIncome())
        this.shopIncome.set({
          total: 0
        });
      console.error(error);
    }
  }

  async findOwnBrandsIncome() {
    try {
      const ownBrandOrders = this.orders()
        .filter((o) =>
          o.lineItems?.some(
            (li) => li.brand && this.OWN_BRANDS.includes(li.brand.toLowerCase())
          )
        )
        .map((o) => ({
          ...o,
          lineItems: o.lineItems.filter(
            (li) => li.brand && this.OWN_BRANDS.includes(li.brand.toLowerCase())
          )
        }));
      const total = ownBrandOrders
        .map((o) =>
          o.lineItems.reduce(
            (sum, li) =>
              (sum +=
                (li.pricePaidWithDiscount || li.pricePaid || 0) *
                (li.quantity || 1)),
            0
          )
        )
        .reduce((sum, t) => (sum += t), 0);
      const createdAt = ownBrandOrders.reduce((last: Date, o) => {
        if (!last || o.createdAt > last) {
          last = o.createdAt;
        }
        return last;
      }, null);
      if (
        !this.ownBrandsIncome()?.total ||
        this.ownBrandsIncome().total < total ||
        !this.ownBrandsIncome()?.createdAt ||
        (createdAt &&
          this.ownBrandsIncome().createdAt.getTime() < createdAt.getTime())
      )
        this.ownBrandsIncome.set({ total, createdAt });
    } catch (error) {
      if (!this.ownBrandsIncome())
        this.ownBrandsIncome.set({
          total: 0,
          createdAt: null
        });
      console.error(error);
    }
  }

  async findCouponIncome() {
    try {
      const couponOrders = this.orders().filter(
        (o) =>
          (o as any).coupon?.category &&
          this.COUPON_CATEGORIES.includes((o as any).coupon.category)
      );
      const total = couponOrders.reduce(
        (sum, o) => (sum += o.totalPrice || 0),
        0
      );
      const createdAt = couponOrders.reduce((last: Date, o) => {
        if (!last || o.createdAt > last) {
          last = o.createdAt;
        }
        return last;
      }, null);
      if (
        !this.couponIncome()?.total ||
        this.couponIncome().total < total ||
        !this.couponIncome()?.createdAt ||
        (createdAt &&
          this.couponIncome().createdAt.getTime() < createdAt.getTime())
      )
        this.couponIncome.set({ total, createdAt });
    } catch (error) {
      if (!this.couponIncome())
        this.couponIncome.set({
          total: 0,
          createdAt: null
        });
      console.error(error);
    }
  }

  async findMensSubscriptions() {
    try {
      const subscriptionsList = await this.subscriptionService.paidToday();
      this.subscriptions.set(subscriptionsList || []);
      const mensSubscriptions = subscriptionsList?.length || 0;
      if (
        !this.mensSubscriptions() ||
        this.mensSubscriptions() < mensSubscriptions
      )
        this.mensSubscriptions.set(mensSubscriptions);
    } catch (error) {
      if (!this.mensSubscriptions()) this.mensSubscriptions.set(0);
      this.subscriptions.set([]);
    }
  }

  async findContributionMargin() {
    try {
      const contributionResponse = await lastValueFrom(
        this.kpiService
          .findContributionMargin()
          .pipe(map((data) => data.result))
      );
      if (
        !this.contributionResponse()?.contributionMargin ||
        this.contributionResponse().contributionMargin !==
          contributionResponse.contributionMargin ||
        !this.contributionResponse()?.contributionMarginPercentage ||
        this.contributionResponse().contributionMarginPercentage !==
          contributionResponse.contributionMarginPercentage
      )
        this.contributionResponse.set(contributionResponse);
    } catch (error) {
      if (!this.contributionResponse())
        this.contributionResponse.set({
          contributionMargin: 0,
          contributionMarginPercentage: 0
        });
      console.error(error);
    }
  }

  async findSubscriptionsRealtimeCount(): Promise<void> {
    try {
      const cached = this.glamSubscriptions();
      if (cached?.total && cached?.createdAt) {
        try {
          const raw = localStorage.getItem(
            ConsolidatedDashboardComponent.CACHE_KEY
          );
          if (raw) {
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed._ts || 0);
            const today = new Date().toDateString();
            const cacheDay = new Date(parsed._ts).toDateString();
            if (
              age < ConsolidatedDashboardComponent.CACHE_TTL &&
              today === cacheDay
            )
              return;
          }
        } catch (error) {
          console.error('Failed to read glam subscriptions cache', error);
        }
      }
      const total = await lastValueFrom(
        this.kpiService.findAnalyticsDashboardRealtimeTotal(0).pipe(
          map((data) => ({
            ...data.result,
            createdAt: new Date(data.result.createdAt)
          }))
        )
      );
      if (
        !this.glamSubscriptions()?.total ||
        !this.glamSubscriptions()?.createdAt ||
        total.total > this.glamSubscriptions().total ||
        total.createdAt.getTime() > this.glamSubscriptions().createdAt.getTime()
      ) {
        this.glamSubscriptions.set(total);
        this.saveGlamSubscriptionsToCache(total);
      }
    } catch (error) {
      console.error(error);
    }
  }

  connect() {
    try {
      this.websocketService.connect(this.onMessage.bind(this));
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  onMessage(message: Message<any>): void {
    console.log(message);
    if (message.topic === Topic.SUBSCRIBER_UPDATE) {
      this.addNewSubscriber({
        ...message.body,
        dateAuthorized: FormUtil.dateUTC(message.body.dateAuthorized)
      });
    }
  }

  addNewSubscriber(subscriber: SubscriberUpdate, attempt = false) {
    if (!this.glamSubscriptions() && !attempt)
      setTimeout(() => {
        this.addNewSubscriber(subscriber, true);
      }, 5000);
    if ([0, 2].includes(subscriber.creditCardPaymentTypeId)) {
      this.glamSubscriptions.update((subs) => ({
        total: subs.total + 1,
        createdAt: subscriber.dateAuthorized
      }));
      this.saveGlamSubscriptionsToCache(this.glamSubscriptions());
    }
  }

  get subscriptionsAvailable(): Array<number> {
    return [0];
  }
}
